/**
 * Main search engine - integrates position analysis, character indexing, and Trie search
 * Provides intelligent search with automatic position detection
 */

import { WordIndex } from '@/types/dictionary';
import { SearchOptions, SearchResult, SearchIndex } from '@/types/search';
import { getOptimalSearchPosition } from './positionAnalyzer';
import { buildSearchIndex } from './characterIndex';
import { Trie } from './trie';
import { loadAllWords, getAvailableWordPrefixes } from '../data/loader';

// Global search index (built once, reused)
let globalSearchIndex: SearchIndex | null = null;
let isIndexBuilt = false;

/**
 * Initialize the search index
 * This should be called once at app startup or on-demand
 * @param words - Optional array of words to build index from. If not provided, creates empty index.
 * @returns Promise that resolves when index is ready
 */
export async function initializeSearchIndex(words?: WordIndex[]): Promise<void> {
  if (isIndexBuilt && globalSearchIndex) {
    return; // Already initialized
  }

  // Build index only if words are provided, otherwise create empty structure
  const wordsToIndex = words || [];
  globalSearchIndex = buildSearchIndex(wordsToIndex);
  isIndexBuilt = true;

  if (words && words.length > 0) {
    console.log(`Search index initialized with ${wordsToIndex.length} words`);
  } else {
    console.log('Search index initialized (lazy loading mode)');
  }
}

/**
 * Get the global search index (initializes if needed)
 */
async function getSearchIndex(): Promise<SearchIndex> {
  if (!globalSearchIndex) {
    // Initialize with empty index for lazy loading
    await initializeSearchIndex();
  }
  return globalSearchIndex!;
}

/**
 * Main search function with intelligent position detection
 * @param query - User's search query
 * @param options - Optional search options
 * @returns Promise resolving to array of search results
 */
export async function searchWords(
  query: string,
  options: SearchOptions = {}
): Promise<SearchResult[]> {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const {
    maxResults = 20,
    minFrequency = 0,
    themes,
    positionMode = 'auto',
    onProgressiveResults,
  } = options;

  // Get or build index
  const searchIndex = await getSearchIndex();

  // Determine search strategy
  const analysis = getOptimalSearchPosition(query);
  const searchPosition = positionMode === 'auto' ? analysis.optimalPosition : positionMode;

  let matchedWords: WordIndex[] = [];

  // Execute search based on optimal position
  if (searchPosition === 1 || query.length === 1) {
    // Standard prefix search using Trie
    // Pass callback for progressive updates
    matchedWords = await searchByPrefix(query, searchIndex, maxResults * 2, {
      onProgressiveUpdate: onProgressiveResults ? (words: WordIndex[]) => {
        // Convert to SearchResult and call the callback
        const progressiveResults = words.map(word => ({
          word,
          score: calculateRelevanceScore(word, query, searchPosition),
          matchPosition: searchPosition,
          matchType: determineMatchType(word.word, query),
        }));
        // Sort by score
        progressiveResults.sort((a, b) => b.score - a.score);
        // Limit and callback
        onProgressiveResults(progressiveResults.slice(0, maxResults));
      } : undefined,
    });
  } else if (searchPosition === 2 && query.length >= 2) {
    // Search by first two characters (with lazy loading)
    matchedWords = await searchByFirstTwoLazy(query[0]!, query[1]!, maxResults * 2);
  } else if (searchPosition === 3 && query.length >= 3) {
    // Search by position 3, then filter by prefix (with lazy loading)
    matchedWords = await searchByPosition3Lazy(query, maxResults * 3);
  }

  // Apply filters
  matchedWords = applyFilters(matchedWords, { minFrequency, themes });

  // Convert to SearchResult objects with scores
  const results: SearchResult[] = matchedWords.map(word => {
    const score = calculateRelevanceScore(word, query, searchPosition);
    const matchType = determineMatchType(word.word, query);

    return {
      word,
      score,
      matchPosition: searchPosition,
      matchType,
    };
  });

  // Sort by score (descending)
  results.sort((a, b) => b.score - a.score);

  // Limit results
  return results.slice(0, maxResults);
}

/**
 * Search by prefix using the prefix trie
 * Loads only the necessary prefix files instead of all words
 * For single-character searches, loads immediately needed data first,
 * then triggers progressive background loading of related prefixes
 * @param prefix - Search prefix
 * @param index - SearchIndex
 * @param maxResults - Maximum results
 * @param options - Options including progressive update callback
 * @returns Array of matching words
 */
async function searchByPrefix(
  prefix: string,
  _index: SearchIndex,
  maxResults: number,
  options?: { onProgressiveUpdate?: (words: WordIndex[]) => void }
): Promise<WordIndex[]> {
  if (prefix.length === 0) {
    return [];
  }

  const trie = new Trie();
  const lowerPrefix = prefix.toLowerCase();
  const firstTwoChars = lowerPrefix.substring(0, 2);

  // Load only the prefix file(s) that could contain matches
  const { loadWordsByPrefix, getAvailableWordPrefixes } = await import('../data/loader');
  const { progressivelyLoadPrefixes } = await import('../data/progressiveLoader');
  const availablePrefixes = getAvailableWordPrefixes();

  // If prefix is 1 character, load intelligently with progressive loading
  if (prefix.length === 1) {
    const relevantPrefixes = availablePrefixes
      .filter(p => p.startsWith(lowerPrefix))
      .sort(); // Alphabetical order

    // Strategy: Load first 2-3 prefixes immediately for quick results,
    // then trigger progressive loading for the rest
    const immediateLoadCount = Math.min(2, relevantPrefixes.length);
    const immediatePrefixes = relevantPrefixes.slice(0, immediateLoadCount);
    const progressivePrefixes = relevantPrefixes.slice(immediateLoadCount);

    // Load initial prefixes immediately
    for (const p of immediatePrefixes) {
      const words = await loadWordsByPrefix(p);
      // Validate and insert words
      if (Array.isArray(words)) {
        words.forEach(word => {
          if (word && word.word) {
            trie.insert(word);
          }
        });
      }
    }

    // Trigger progressive background loading for remaining prefixes
    // This happens after 1000ms delay and loads sequentially with 500ms between each
    if (progressivePrefixes.length > 0) {
      // Don't await - let it run in background
      progressivelyLoadPrefixes(lowerPrefix, {
        initialDelay: 1000,
        betweenDelay: 500, // 500ms delay between each file load
        onPrefixLoaded: options?.onProgressiveUpdate ? (_prefix, words) => {
          // Insert new words into the trie
          if (Array.isArray(words)) {
            words.forEach(word => {
              if (word && word.word) {
                trie.insert(word);
              }
            });
          }
          // Call the callback with updated search results
          const updatedResults = trie.search(lowerPrefix, maxResults);

          // Deduplicate by xmlId to prevent React key warnings
          const seenIds = new Set<string>();
          const dedupedResults = updatedResults.filter(word => {
            const id = word.xmlId || word.word;
            if (seenIds.has(id)) {
              return false;
            }
            seenIds.add(id);
            return true;
          });

          options.onProgressiveUpdate!(dedupedResults);
        } : undefined,
      }).catch(error => {
        console.error('Progressive loading failed:', error);
      });
    }
  } else {
    // If prefix is 2+ characters, load only the specific prefix file
    if (availablePrefixes.includes(firstTwoChars)) {
      const words = await loadWordsByPrefix(firstTwoChars);
      // Validate and insert words
      if (Array.isArray(words)) {
        words.forEach(word => {
          if (word && word.word) {
            trie.insert(word);
          }
        });
      }
    }
  }

  return trie.search(prefix, maxResults);
}

/**
 * Search by first two characters with lazy loading
 * @param firstChar - First character
 * @param secondChar - Second character
 * @param maxResults - Maximum results
 * @returns Array of matching words
 */
async function searchByFirstTwoLazy(
  firstChar: string,
  secondChar: string,
  maxResults: number
): Promise<WordIndex[]> {
  const lowerFirst = firstChar.toLowerCase();
  const lowerSecond = secondChar.toLowerCase();
  const prefix = lowerFirst + lowerSecond;

  // Load the specific prefix file
  const { loadWordsByPrefix, getAvailableWordPrefixes } = await import('../data/loader');
  const availablePrefixes = getAvailableWordPrefixes();

  if (!availablePrefixes.includes(prefix)) {
    return [];
  }

  const words = await loadWordsByPrefix(prefix);

  // Sort by frequency and return top results
  words.sort((a, b) => b.frequency - a.frequency);
  return words.slice(0, maxResults);
}

/**
 * Search by position 3 with lazy loading
 * Loads all files that could contain matches, then filters by position 3
 * @param query - Full query string (at least 3 characters)
 * @param maxResults - Maximum results
 * @returns Array of matching words
 */
async function searchByPosition3Lazy(
  query: string,
  maxResults: number
): Promise<WordIndex[]> {
  const lowerQuery = query.toLowerCase();
  const firstTwoChars = lowerQuery.substring(0, 2);
  const char3 = lowerQuery[2]!;

  // For position-3 search, we need to load the prefix file for the first 2 chars
  const { loadWordsByPrefix, getAvailableWordPrefixes } = await import('../data/loader');
  const availablePrefixes = getAvailableWordPrefixes();

  if (!availablePrefixes.includes(firstTwoChars)) {
    return [];
  }

  const words = await loadWordsByPrefix(firstTwoChars);

  // Filter by third character and prefix match
  const matches = words.filter(word => {
    const wordLower = word.word.toLowerCase();
    return wordLower.length >= 3 &&
           wordLower[2] === char3 &&
           wordLower.startsWith(lowerQuery);
  });

  // Sort by frequency and return top results
  matches.sort((a, b) => b.frequency - a.frequency);
  return matches.slice(0, maxResults);
}

/**
 * Apply filters to search results
 * @param words - Array of words to filter
 * @param filters - Filter criteria
 * @returns Filtered array
 */
function applyFilters(
  words: WordIndex[],
  filters: { minFrequency?: number; themes?: string[] }
): WordIndex[] {
  let filtered = words;

  if (filters.minFrequency !== undefined && filters.minFrequency > 0) {
    filtered = filtered.filter(word => word.frequency >= filters.minFrequency!);
  }

  if (filters.themes && filters.themes.length > 0) {
    filtered = filtered.filter(word =>
      word.themes.some(theme => filters.themes!.includes(theme))
    );
  }

  return filtered;
}

/**
 * Calculate relevance score for a word
 * @param word - WordIndex object
 * @param query - User's query
 * @param matchPosition - Which position matched
 * @returns Score (0-100)
 */
function calculateRelevanceScore(
  word: WordIndex,
  query: string,
  _matchPosition: number
): number {
  let score = word.frequency; // Base score from frequency (0-100)

  const wordLower = word.word.toLowerCase();
  const queryLower = query.toLowerCase();

  // Exact match bonus
  if (wordLower === queryLower) {
    score += 50;
  }

  // Prefix match bonus (stronger than contains)
  if (wordLower.startsWith(queryLower)) {
    score += 20;
  }

  // Length similarity bonus (prefer words closer to query length)
  const lengthDiff = Math.abs(word.word.length - query.length);
  score -= lengthDiff * 0.5;

  // Academic vocabulary bonus
  if (word.avlIncluded) {
    score += 5;
  }
  if (word.aawlIncluded) {
    score += 5;
  }

  // Log-likelihood ratio bonus (academic importance)
  score += word.llRatio * 0.5;

  // Normalize to 0-100 range
  return Math.max(0, Math.min(100, score));
}

/**
 * Determine match type
 * @param word - Word that matched
 * @param query - User's query
 * @returns Match type
 */
function determineMatchType(
  word: string,
  query: string
): 'exact' | 'prefix' | 'contains' {
  const wordLower = word.toLowerCase();
  const queryLower = query.toLowerCase();

  if (wordLower === queryLower) {
    return 'exact';
  } else if (wordLower.startsWith(queryLower)) {
    return 'prefix';
  } else {
    return 'contains';
  }
}

/**
 * Search for words matching specific criteria
 * @param criteria - Search criteria
 * @returns Promise resolving to array of matching words
 */
export async function advancedSearch(criteria: {
  firstChar?: string;
  secondChar?: string;
  thirdChar?: string;
  minLength?: number;
  maxLength?: number;
  themes?: string[];
  minFrequency?: number;
}): Promise<WordIndex[]> {
  const allWords = await loadAllWords();
  let results = allWords;

  if (criteria.firstChar) {
    const char = criteria.firstChar.toLowerCase();
    results = results.filter(w => w.word.toLowerCase()[0] === char);
  }

  if (criteria.secondChar) {
    const char = criteria.secondChar.toLowerCase();
    results = results.filter(w => w.word.toLowerCase()[1] === char);
  }

  if (criteria.thirdChar) {
    const char = criteria.thirdChar.toLowerCase();
    results = results.filter(w => w.word.toLowerCase()[2] === char);
  }

  if (criteria.minLength) {
    results = results.filter(w => w.word.length >= criteria.minLength!);
  }

  if (criteria.maxLength) {
    results = results.filter(w => w.word.length <= criteria.maxLength!);
  }

  if (criteria.themes && criteria.themes.length > 0) {
    results = results.filter(w => w.themes.some(t => criteria.themes!.includes(t)));
  }

  if (criteria.minFrequency) {
    results = results.filter(w => w.frequency >= criteria.minFrequency!);
  }

  // Sort by frequency
  results.sort((a, b) => b.frequency - a.frequency);

  return results;
}

/**
 * Get search suggestions based on partial input
 * @param query - Partial query
 * @param limit - Maximum suggestions
 * @returns Promise resolving to array of suggested words
 */
export async function getSearchSuggestions(
  query: string,
  limit: number = 5
): Promise<string[]> {
  const results = await searchWords(query, { maxResults: limit });
  return results.map(r => r.word.word);
}

/**
 * Clear the search index (useful for testing or reloading)
 */
export function clearSearchIndex(): void {
  globalSearchIndex = null;
  isIndexBuilt = false;
}

/**
 * Get search index statistics
 */
export async function getSearchStats(): Promise<{
  totalWords: number;
  indexBuilt: boolean;
  availablePrefixes: string[];
}> {
  return {
    totalWords: globalSearchIndex ? (await loadAllWords()).length : 0,
    indexBuilt: isIndexBuilt,
    availablePrefixes: getAvailableWordPrefixes(),
  };
}
